import { FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { useState } from 'react';
import { FaBirthdayCake, FaLinkedin, FaUser, FaUsers } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import { GoDotFill } from 'react-icons/go';
import { IoLanguage } from 'react-icons/io5';
import { MdEmail, MdFeedback, MdPassword, MdPhoneInTalk } from 'react-icons/md';
import { RiCustomerService2Fill, RiEdit2Fill } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sectionAnimVariants } from '../../constants/animationVariants';
import { useAuthContext } from '../../contexts/AuthProvider';
import useAssociateActions from '../../hooks/APIHooks/users/useAssociateActions';
import useAuthActions from '../../hooks/APIHooks/users/useAuthActions';
import { AssociatePasswordChangeBody } from '../../types/associates';
import { User, UserProfile } from '../../types/users';
import { showFullName, showPicture } from '../../utils/commonFunctions';
import { deleteToken } from '../../utils/cookieHandler';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import AcademiaRating from '../CommonComps/AcademiaRating';
import AcademiaSectionWrapper from '../CommonComps/AcademiaSectionWrapper';
import AvailabilityBadge from '../CommonComps/Badges/AvailabilityBadge';
import PersonalInfoCard from '../CommonComps/Cards/PersonalInfoCard';
import ChangePasswordModal from '../CommonComps/Modals/ChangePasswordModal';
import CreateEditAssociateModal from '../CommonComps/Modals/CreateEditAssociateModal';

const PersonalInfo = ({
  currentUser,
  userProfile,
}: {
  currentUser: User;
  userProfile: UserProfile | undefined;
}) => {
  // destructuring the current user object here
  const {
    username,
    first_name,
    middle_name,
    last_name,
    email,
    isd_code,
    phone,
    profile_picture,
    role_name,
    country_name,
    associate_rating,
    total_clients,
    feedback_count,
    total_languages,
    credits_per_appointments,
  } = currentUser || {};

  // destructuring user profile object here
  const {
    linkedin_profile,
    bio,
    date_of_birth,
    address,
    province_name,
    city_name,
    postal_code,
  } = userProfile || {};

  const splittedLinkedinUrl = linkedin_profile?.split('/');

  // integration of react hooks here
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showChangeModal, setShowChangeModal] = useState<boolean>(false);

  // integration of react-router-dom hooks here
  const navigate = useNavigate();

  // integration of context hooks here
  const { setCurrentUserId } = useAuthContext();

  // integration of API hooks here
  const { changeAssociatePasswordHandler } = useAssociateActions('');
  const { userLogoutHandler } = useAuthActions();

  // handler function to handle change password feature declared here
  const changePasswordHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      const body: AssociatePasswordChangeBody = {
        current_password: values.current_password,
        password: values.new_password,
        is_first_login: false,
        updated_by: currentUser?.username as string,
      };

      await changeAssociatePasswordHandler
        .mutateAsync({ associateId: currentUser.id, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Password Changed!! Please, Login Again..');

            setShowChangeModal(false);

            userLogoutHandler.mutateAsync().then((res) => {
              if (res.is_success) {
                deleteToken();

                setCurrentUserId(0);

                sessionStorage.clear();
                localStorage.clear();

                navigate('/');
              }
            });
          } else {
            toast.error(res.message || 'Password Change Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering personal info section component here
  return (
    <motion.section
      variants={sectionAnimVariants}
      initial='initial'
      animate='animate'
      className='relative p-3 md:p-5 shadow-academia-sm rounded-xl mx-auto'
    >
      <div className='space-y-5'>
        <div className='flex items-start gap-10'>
          <div className='flex items-center justify-center'>
            {profile_picture ? (
              <div className='relative size-28'>
                <div className='size-full rounded-full overflow-hidden'>
                  <img
                    className='size-full object-cover'
                    src={showPicture(profile_picture)}
                    alt={`avatar of ${username}`}
                  />
                </div>
                <AvailabilityBadge />
              </div>
            ) : (
              <div
                title='Online'
                className='relative flex items-center justify-center bg-base-light-gray size-28 rounded-full'
              >
                <FaUser className='size-16 text-gray-300' />
                <AvailabilityBadge />
              </div>
            )}
          </div>

          <div className=''>
            <div className='space-y-1'>
              <h3 className='text-3xl text-base-dark-green font-extrabold'>
                {showFullName(first_name, middle_name, last_name)}
              </h3>

              <div className='flex items-center gap-0.5 text-sm font-semibold'>
                <span className='text-legal-dark-green'>@{username}</span>

                <GoDotFill className='text-gray-500' />

                <span
                  className={`${
                    role_name === 'Admin'
                      ? 'text-base-purple'
                      : role_name === 'Associate'
                      ? 'text-base-light-blue'
                      : 'text-base-orange'
                  }`}
                >
                  {role_name}
                </span>
              </div>
            </div>

            <p className='flex items-start justify-center gap-1 text-base-dark-blue'>
              <span className='pt-1'>
                <FaLocationDot />
              </span>
              <span className='w-[180px] md:w-fit'>
                {address}, {city_name}, {province_name}, {postal_code},{' '}
                {country_name}
              </span>
            </p>

            {linkedin_profile ? (
              <p className='flex items-center justify-center gap-2 text-base-orange'>
                <FaLinkedin className='size-5' />
                <a
                  href={linkedin_profile}
                  className='font-medium hover:opacity-70 hover:underline duration-300'
                  target='_blank'
                  rel='noreferrer'
                >
                  {`/in/${
                    splittedLinkedinUrl
                      ? splittedLinkedinUrl[
                          Number(splittedLinkedinUrl?.length) - 1
                        ]
                        ? splittedLinkedinUrl[
                            Number(splittedLinkedinUrl?.length) - 1
                          ]
                        : splittedLinkedinUrl[
                            Number(splittedLinkedinUrl?.length) - 2
                          ]
                      : null
                  }`}
                </a>
              </p>
            ) : null}

            {currentUser.role_name === 'Associate' ? (
              <AcademiaRating
                size={45}
                ratingValue={associate_rating}
                isReadonly
                extraClassNames='justify-center !my-3'
              />
            ) : null}

            {bio ? (
              <div className='w-full md:w-4/5 mx-auto text-left'>
                <AcademiaSectionWrapper sectionTitle={'Bio'}>
                  <p className='text-base-dark-blue'>{bio}</p>
                </AcademiaSectionWrapper>
              </div>
            ) : null}
          </div>
        </div>

        <div className='grid md:grid-cols-2 2xl:grid-cols-3 gap-5'>
          <PersonalInfoCard
            icon={<MdEmail className='size-20 text-base-purple' />}
            value={email}
            label='Email'
            color='#845ADF'
            valueFontSize='text-xl'
            isReverse
          />

          <PersonalInfoCard
            icon={<MdPhoneInTalk className='size-20 text-base-orange' />}
            value={`${isd_code}${phone}`}
            label='Phone number'
            color='#FF8042'
            valueFontSize='text-xl'
            isReverse
          />

          <PersonalInfoCard
            icon={<FaBirthdayCake className='size-20 text-base-light-blue' />}
            value={
              date_of_birth
                ? moment(date_of_birth).format('Do MMMM, YYYY')
                : 'N/A'
            }
            label='Date of Birth'
            color='#00BCEF'
            valueFontSize='text-xl'
            isReverse
            spanClassName='md:col-span-2 2xl:col-span-1'
          />
        </div>

        <div className='grid md:grid-cols-2 2xl:grid-cols-4 gap-5 pt-5 border-t-2 border-base-cyan/70'>
          <PersonalInfoCard
            icon={<FaUsers className='size-20 text-base-light-blue' />}
            value={total_clients}
            label='Clients Handled'
            color='#00BCEF'
          />

          <PersonalInfoCard
            icon={<MdFeedback className='size-20 text-base-cyan' />}
            value={feedback_count}
            label='Client Reviews'
            color='#1ABC9C'
          />

          <PersonalInfoCard
            icon={<IoLanguage className='size-20 text-base-purple' />}
            value={total_languages}
            label='Languages Proficient'
            color='#845ADF'
          />

          <PersonalInfoCard
            icon={
              <RiCustomerService2Fill className='size-20 text-base-orange' />
            }
            value={credits_per_appointments?.length || 0}
            label='Services provided'
            color='#FF8042'
          />
        </div>
      </div>

      <div className='absolute right-5 top-5 space-y-2'>
        <button
          title='Edit Personal Information'
          onClick={() => setShowEditModal(true)}
          className='flex items-center justify-center bg-gray-200 text-base-dark-gray size-9 rounded-full hover:scale-110 hover:bg-base-light-blue hover:text-base-dark-blue active:scale-95 duration-300'
        >
          <RiEdit2Fill className='size-5' />
        </button>

        <button
          title='Change Password'
          onClick={() => setShowChangeModal(true)}
          className='flex items-center justify-center bg-gray-200 text-base-dark-gray size-9 rounded-full hover:scale-110 hover:bg-base-light-blue hover:text-base-dark-blue active:scale-95 duration-300'
        >
          <MdPassword className='size-5' />
        </button>
      </div>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showEditModal && (
          <CreateEditAssociateModal
            title='update profile'
            associate={currentUser}
            associateProfile={userProfile}
            handleModalClose={() => setShowEditModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showChangeModal && (
          <ChangePasswordModal
            handleModalClose={() => setShowChangeModal(false)}
            changePasswordFn={changePasswordHandler}
            isRequestedFromUser
          />
        )}
      </AnimatePresence>
    </motion.section>
  );
};

export default PersonalInfo;
